import {
  Box,
  Container,
  Typography,
  Skeleton,
  useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { CardSchedule } from './components/CardSchedule';
import Slider from 'react-slick';
import { theme } from '@/styles/theme';
import { useAppointmentsService } from '@/data/services/useAppointmentsService';
import { useAuthStore } from '@/stores/AuthStore';
import { AppError } from '@/utils/AppError';

import { format, compareAsc } from 'date-fns';
import AddIcon from '@mui/icons-material/Add';

import './components/style.css';
import { capitalizeFirstLetterOfEachWord } from '@/utils/getInitials';
import { getLocale } from '@/utils/formatedLanguage';
import EmptyState from '@/components/emptyState/EmptyState';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSnackbarStoreComponent } from '@/stores/useSnackbar';
import { BannerApp } from './components/BannerApp/BannerApp';
import { dateFormatter } from '@/utils/dateFormatter';
interface ArrowProps {
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
}

enum EnumServiceType {
  ONLINE = '2',
  INPERSON = '1',
}

export const Home = () => {
  const { t } = useTranslation(['home', 'schedule', 'appointmentDetails']);

  const { getAppointmentsCardList } = useAppointmentsService();
  const { showSnackbar } = useSnackbarStoreComponent();
  const [appointments, setAppointments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuthStore();
  const userFullName = `${user?.name || ''} ${user?.surname || ''}`;
  const navigate = useNavigate();
  const location = useLocation();
  const { showScheduleSuccess, showErrorGetReschedule, showReScheduleSuccess } =
    location.state || {};
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { getUserLocalTimeOrClinicTime } = dateFormatter();

  useEffect(() => {
    if (showScheduleSuccess) {
      showSnackbar(t('schedule:screen_schedule_success'), 'success', '360px');
      navigate('/home', { replace: true, state: {} });
    }
    if (showReScheduleSuccess) {
      showSnackbar(t('schedule:screen_reschedule_success'), 'success', '360px');
      navigate('/home', { replace: true, state: {} });
    }
    if (showErrorGetReschedule) {
      showSnackbar(t('schedule:screen_get_reschedule_error'), 'error', '360px');
      navigate('/home', { replace: true, state: {} });
    }
  }, []);

  const processAppointmentData = (item: any) => {
    const locale = getLocale();
    const parsedDate = getUserLocalTimeOrClinicTime(item.date, item.time, item.location.gmt);

    const dateFormatted = item.date
      ? format(parsedDate.asDate, 'PPP', { locale })
      : 'Data não informada';
    const timeFormatted = item.time
      ? format(parsedDate.asDate, 'hh:mm a', { locale })
      : 'Hora não informada';

    return {
      id: item.id,
      patientName: item.dependent?.name
        ? capitalizeFirstLetterOfEachWord(
            `${item.dependent.name} ${item.dependent.surname}`
          )
        : capitalizeFirstLetterOfEachWord(userFullName) ||
          'Nome do paciente não informado',

      avatar: item.dependent
        ? item.dependent.profilePhotoUrl || ''
        : user?.picture || '',
      appointmentLink: item.appointmentLink || null,
      type:
        item.appointmentTypeId === EnumServiceType.INPERSON
          ? `${t('in_person')} | ${
              item.location?.name || 'Endereço não informado'
            }`
          : t('online'),
      location: `${item.location?.name || 'Endereço não informado'}`,
      doctorName: `Dr(a) ${item.doctor?.firstName || ''} ${
        item.doctor?.lastName || ''
      }`,
      date: dateFormatted,
      time: `${timeFormatted} ${parsedDate.timeZone}`,
    };
  };

  const fetchAppointments = async () => {
    setLoading(true);
    getAppointmentsCardList()
      .then((data) => {
        console.log('data>>>', data);

        const processedData = data
          .map(processAppointmentData)
          .filter((item) => item.date !== null)
          .sort((a, b) => compareAsc(new Date(a.date), new Date(b.date)))
          .slice(0, 5);
        setAppointments(processedData);
      })
      .catch((err: AppError) => {
        showSnackbar(t(err.message), 'error');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchAppointments();
  }, [getAppointmentsCardList, showSnackbar, t]);

  const handleViewDetails = async (appointmentId: string) => {
    navigate(`/appointment/${appointmentId}`);
  };

  function SampleNextArrow(props: ArrowProps) {
    const { className, style, onClick } = props;
    return (
      <div
        className={className}
        style={{ ...style, display: 'block' }}
        onClick={onClick}
      />
    );
  }
  function SamplePrevArrow(props: ArrowProps) {
    const { className, style, onClick } = props;
    return (
      <div
        className={className}
        style={{ ...style, display: 'block' }}
        onClick={onClick}
      />
    );
  }
  //Configuração do slider
  const settings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: appointments.length === 1 ? 1 : 2.2,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: appointments.length === 1 ? 1 : 2.2,
          slidesToScroll: 1,
        },
      },
    ],
    nextArrow: <SampleNextArrow />,
    prevArrow: <SamplePrevArrow />,
  };

  return (
    <Container maxWidth="lg" sx={styles.container}>
      <Box sx={styles.content}>
        <Typography sx={{ fontWeight: 600 }}>
          {t('title_next_scheduling')}
        </Typography>
        <Box sx={styles.buttonsContainer}>
          {appointments?.length > 1 && (
            <Typography
              component="a"
              onClick={() => navigate('/appointments')}
              sx={styles.link}
            >
              {t('see_more')}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ mt: 2, mb: 2 }}>
        {loading ? (
          <Skeleton
            variant="rectangular"
            height={200}
            animation="wave"
            sx={{ borderRadius: 2, mb: 2 }}
          />
        ) : appointments?.length > 0 ? (
          <Slider {...settings}>
            {appointments.map((appointment: any) => (
              <Box key={appointment.id} sx={{ padding: '0 10px' }}>
                <CardSchedule
                  appointment={appointment}
                  onViewDetails={() => handleViewDetails(appointment.id)}
                />
              </Box>
            ))}
          </Slider>
        ) : (
          <EmptyState
            title={t('empty_state_title')}
            buttonAction={() => navigate('/schedule')}
            buttonTitle={t('schedule_consultation') as string}
            description={t('empty_state_description')}
            isHidden={false}
          />
        )}
      </Box>
      <Box sx={{ mt: 5 }}></Box>
      <BannerApp onScheduleClick={() => navigate('/schedule')} />
      {appointments?.length !== 0 && isSmallScreen && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Typography
            component="a"
            onClick={() => navigate('/schedule')}
            sx={(theme) => ({
              ...styles.scheduleLink,
              width: '18.9rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              [theme.breakpoints.down('sm')]: {
                position: 'fixed',
                bottom: 20,
                right: 20,
              },
            })}
          >
            <AddIcon sx={{ fontSize: '1.6rem' }} />
            {t('home:schedule_consultation')}
          </Typography>
        </Box>
      )}
    </Container>
  );
};

const styles = {
  container: {
    backgroundColor: theme.palette.secondary.contrastText,
    borderRadius: 2,
    paddingTop: 1.5,
    paddingBottom: 1.5,
    marginTop: 5,
    marginInline: 'auto',
    [theme.breakpoints.down('sm')]: {
      paddingRight: 2,
      paddingLeft: 2,
      paddingBottom: 7,
    },
  },
  content: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  buttonsContainer: {
    display: 'flex',
    gap: 2,
    alignItems: 'center',
  },
  link: {
    fontWeight: 500,
    fontSize: '1.4rem',
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      color: theme.palette.primary.dark,
      textDecoration: 'underline',
      cursor: 'pointer',
    },
  },
  scheduleLink: {
    fontWeight: 500,
    fontSize: '1.4rem',
    color: theme.palette.primary.contrastText,
    padding: '1rem',
    borderRadius: 6,
    backgroundColor: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`,
    textDecoration: 'none',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      cursor: 'pointer',
    },
  },
};
